import React, { useEffect, useState } from 'react';
import { 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper,
  TablePagination,
  TableSortLabel,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { useTranslation } from '../../hooks/useTranslation';
import api from '../../utils/api';

interface User {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  role: string;
  joinedDate: string;
  createdAt: string;
  updatedAt: string;
  lastLoginDate: string;
}

interface Pagination {
  total: number;
  page: number;
  limit: number;
  pages: number;
}

interface ApiResponse {
  success: boolean;
  data: User[];
  pagination: Pagination;
}

type SortField = 'createdAt' | 'lastLoginDate' | 'firstName' | 'lastName' | 'email';
type SortOrder = 'asc' | 'desc';

export const UsersManagement: React.FC = () => {
  const { t } = useTranslation();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<Pagination>({
    total: 0,
    page: 0,
    limit: 10,
    pages: 0
  });
  const [sortField, setSortField] = useState<SortField>('createdAt');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  const fetchUsers = async (page: number, limit: number, sortBy?: SortField, order?: SortOrder) => {
    try {
      const sortByParam = sortBy || sortField;
      const orderParam = order || sortOrder;
      const response = await api.get<ApiResponse>(`/users?page=${page + 1}&limit=${limit}&sortBy=${sortByParam}&sortOrder=${orderParam}`);
      setUsers(response.data.data);
      setPagination(response.data.pagination);
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch users');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers(0, 10);
  }, []);

  const handleChangePage = (_: unknown, newPage: number) => {
    fetchUsers(newPage, pagination.limit, sortField, sortOrder);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newLimit = parseInt(event.target.value, 10);
    fetchUsers(0, newLimit, sortField, sortOrder);
  };

  const handleSort = (field: SortField) => {
    const newOrder = sortField === field && sortOrder === 'desc' ? 'asc' : 'desc';
    setSortField(field);
    setSortOrder(newOrder);
    fetchUsers(0, pagination.limit, field, newOrder);
  };

  const handleSortFieldChange = (event: any) => {
    const newField = event.target.value as SortField;
    setSortField(newField);
    fetchUsers(0, pagination.limit, newField, sortOrder);
  };

  const handleSortOrderChange = (event: any) => {
    const newOrder = event.target.value as SortOrder;
    setSortOrder(newOrder);
    fetchUsers(0, pagination.limit, sortField, newOrder);
  };

  return (
    <div>
      <Typography variant="h4" sx={{ mb: 3 }}>{t('admin.tabs.users')}</Typography>
      
      {/* Sorting Controls */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>{t('admin.sorting.sortBy')}</InputLabel>
          <Select
            value={sortField}
            label={t('admin.sorting.sortBy')}
            onChange={handleSortFieldChange}
          >
            <MenuItem value="createdAt">{t('admin.sorting.joinedDate')}</MenuItem>
            <MenuItem value="lastLoginDate">{t('admin.sorting.lastLogin')}</MenuItem>
            <MenuItem value="firstName">{t('admin.sorting.firstName')}</MenuItem>
            <MenuItem value="lastName">{t('admin.sorting.lastName')}</MenuItem>
            <MenuItem value="email">{t('admin.sorting.email')}</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>{t('admin.sorting.order')}</InputLabel>
          <Select
            value={sortOrder}
            label={t('admin.sorting.order')}
            onChange={handleSortOrderChange}
          >
            <MenuItem value="desc">{t('admin.sorting.descending')}</MenuItem>
            <MenuItem value="asc">{t('admin.sorting.ascending')}</MenuItem>
          </Select>
        </FormControl>
      </Box>
      
      {loading ? (
        <Typography>Loading...</Typography>
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : (
        <Paper>
          <TableContainer>
            <Table sx={{ minWidth: 650 }} aria-label="users table">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'email'}
                      direction={sortField === 'email' ? sortOrder : 'asc'}
                      onClick={() => handleSort('email')}
                    >
                      {t('admin.sorting.email')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'firstName'}
                      direction={sortField === 'firstName' ? sortOrder : 'asc'}
                      onClick={() => handleSort('firstName')}
                    >
                      {t('admin.sorting.firstName')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'lastName'}
                      direction={sortField === 'lastName' ? sortOrder : 'asc'}
                      onClick={() => handleSort('lastName')}
                    >
                      {t('admin.sorting.lastName')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>{t('auth.phoneNumber')}</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'createdAt'}
                      direction={sortField === 'createdAt' ? sortOrder : 'asc'}
                      onClick={() => handleSort('createdAt')}
                    >
                      {t('admin.sorting.joinedDate')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === 'lastLoginDate'}
                      direction={sortField === 'lastLoginDate' ? sortOrder : 'asc'}
                      onClick={() => handleSort('lastLoginDate')}
                    >
                      {t('admin.sorting.lastLogin')}
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user._id}>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.firstName}</TableCell>
                    <TableCell>{user.lastName}</TableCell>
                    <TableCell>{user.phoneNumber}</TableCell>
                    <TableCell>{user.role}</TableCell>
                    <TableCell>{new Date(user.joinedDate).toLocaleDateString()}</TableCell>
                    <TableCell>
                      {user.lastLoginDate 
                        ? new Date(user.lastLoginDate).toLocaleDateString() 
                        : t('admin.sorting.never')
                      }
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={pagination.total}
            rowsPerPage={pagination.limit}
            page={pagination.page - 1}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      )}
    </div>
  );
}; 